import { Customer, ShopSettings } from '@/types/models';
import { CustomerLedgerSummary } from '@/services/customers';

export interface PrintCustomerStatementData {
  shop: ShopSettings;
  customer: Customer;
  ledger: CustomerLedgerSummary;
  dateRange?: { from?: string; to?: string };
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return 'Rs. 0';
  return `Rs. ${amount.toLocaleString('en-PK')}`;
}

export function generateCustomerStatementHtml(data: PrintCustomerStatementData): string {
  const { shop, customer, ledger } = data;

  const phoneList: string[] = [];
  if (shop.phone) phoneList.push(shop.phone);
  if (shop.phone_secondary) phoneList.push(shop.phone_secondary);
  if (shop.phone_tertiary) phoneList.push(shop.phone_tertiary);

  const rows = ledger.entries.map((e) => `
    <tr>
      <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; font-family: monospace;">${formatDate(e.date)}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; font-weight: bold; font-family: monospace;">${e.reference}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px;">${e.description}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; text-align: right;">${e.debit > 0 ? formatMoney(e.debit) : '—'}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; text-align: right; color: #15803d;">${e.credit > 0 ? formatMoney(e.credit) : '—'}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; text-align: right; font-weight: bold;">${formatMoney(e.runningBalance)}</td>
    </tr>
  `).join('');

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>Statement - ${customer.name}</title>
      <style>
        @page { size: A4; margin: 15mm; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          color: #111827;
          background: #ffffff;
          margin: 0;
          padding: 0;
          font-size: 12px;
          line-height: 1.4;
        }
        .header { display: flex; justify-content: space-between; border-bottom: 2px solid #16a34a; padding-bottom: 12px; margin-bottom: 16px; }
        .title { font-size: 20px; font-weight: 800; color: #16a34a; margin: 0; }
        .meta-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; margin-bottom: 16px; display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .summary-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
        .card { background: #f3f4f6; border-radius: 6px; padding: 8px 12px; }
        .card-label { font-size: 10px; text-transform: uppercase; color: #6b7280; font-weight: 700; }
        .card-val { font-size: 14px; font-weight: 800; color: #111827; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th { background: #f3f4f6; text-align: left; padding: 8px; font-size: 10px; text-transform: uppercase; color: #4b5563; font-weight: 700; border-bottom: 2px solid #d1d5db; }
        .footer { border-top: 1px solid #e5e7eb; padding-top: 12px; font-size: 11px; text-align: center; color: #6b7280; }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <h1 class="title">${shop.shop_name || 'AL KUWAIT TECH'}</h1>
          <p style="margin: 2px 0 0 0; color: #4b5563; font-size: 11px;">${shop.slogan || 'Retail & Repair Solutions'}</p>
          <p style="margin: 2px 0 0 0; color: #6b7280; font-size: 10px;">${shop.address || ''}${shop.city ? ', ' + shop.city : ''}</p>
          ${phoneList.length > 0 ? `<p style="margin: 2px 0 0 0; color: #6b7280; font-size: 10px;">Tel: ${phoneList.join(' | ')}</p>` : ''}
        </div>
        <div style="text-align: right;">
          <h2 style="margin: 0; font-size: 16px; font-weight: 800; color: #111827;">CUSTOMER ACCOUNT STATEMENT</h2>
          <p style="margin: 4px 0 0 0; font-size: 11px; color: #4b5563;">Date: <strong>${formatDate(new Date().toISOString())}</strong></p>
        </div>
      </div>

      <div class="meta-box">
        <div>
          <p style="margin: 0; font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase;">Customer Information</p>
          <p style="margin: 4px 0 0 0; font-size: 14px; font-weight: 800; color: #111827;">${customer.name}</p>
          ${customer.phone ? `<p style="margin: 2px 0 0 0; color: #4b5563;">Phone: ${customer.phone}</p>` : ''}
          ${customer.email ? `<p style="margin: 2px 0 0 0; color: #4b5563;">Email: ${customer.email}</p>` : ''}
          ${customer.address ? `<p style="margin: 2px 0 0 0; color: #4b5563;">Address: ${customer.address}</p>` : ''}
        </div>
        <div style="text-align: right;">
          <p style="margin: 0; font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase;">Account Status</p>
          <p style="margin: 4px 0 0 0; font-size: 16px; font-weight: 900; color: ${ledger.outstandingBalance > 0 ? '#b91c1c' : '#15803d'};">
            Outstanding: ${formatMoney(ledger.outstandingBalance)}
          </p>
          <p style="margin: 2px 0 0 0; font-size: 11px; color: #4b5563;">Customer Code: <strong>${customer.customer_code || customer.id.slice(0, 8)}</strong></p>
        </div>
      </div>

      <div class="summary-cards">
        <div class="card">
          <div class="card-label">Total Invoiced</div>
          <div class="card-val">${formatMoney(ledger.totalSales)}</div>
        </div>
        <div class="card">
          <div class="card-label">Total Paid In</div>
          <div class="card-val" style="color: #15803d;">${formatMoney(ledger.totalPaymentsReceived)}</div>
        </div>
        <div class="card">
          <div class="card-label">Return Credits</div>
          <div class="card-val">${formatMoney(ledger.totalReturnCredit)}</div>
        </div>
        <div class="card" style="background: ${ledger.outstandingBalance > 0 ? '#fef2f2' : '#f0fdf4'}; border: 1px solid ${ledger.outstandingBalance > 0 ? '#fecaca' : '#bbf7d0'};">
          <div class="card-label" style="color: ${ledger.outstandingBalance > 0 ? '#991b1b' : '#166534'};">Closing Balance</div>
          <div class="card-val" style="color: ${ledger.outstandingBalance > 0 ? '#b91c1c' : '#15803d'};">${formatMoney(ledger.outstandingBalance)}</div>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th style="width: 80px;">Date</th>
            <th style="width: 110px;">Reference</th>
            <th>Description</th>
            <th style="text-align: right; width: 90px;">Debit (+)</th>
            <th style="text-align: right; width: 90px;">Credit (-)</th>
            <th style="text-align: right; width: 100px;">Balance</th>
          </tr>
        </thead>
        <tbody>
          ${rows.length > 0 ? rows : '<tr><td colspan="6" style="padding: 16px; text-align: center; color: #9ca3af;">No transaction history found.</td></tr>'}
        </tbody>
      </table>

      <div class="footer">
        <p style="margin: 0; font-weight: 600;">${shop.invoice_footer || 'Thank you for your business with Al Kuwait Tech!'}</p>
        <p style="margin: 2px 0 0 0; font-size: 10px;">Generated automatically on ${new Date().toLocaleString('en-PK')}</p>
      </div>
    </body>
    </html>
  `;
}

export function printCustomerStatement(data: PrintCustomerStatementData): void {
  const html = generateCustomerStatementHtml(data);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) return;

  doc.open();
  doc.write(html);
  doc.close();

  iframe.contentWindow?.focus();
  setTimeout(() => {
    iframe.contentWindow?.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }, 300);
}
